
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Folder, RefreshCw } from "lucide-react";
import { SearchAndSortControls } from "./components/SearchAndSortControls";
import { FileList } from "./components/FileList";
import { useFileOperations } from "./hooks/useFileOperations";
import { useFileFiltering } from "./hooks/useFileFiltering";
import type { FileObject } from "@supabase/storage-js";

interface StorageBrowserProps {
  bucketName: string;
  onFileSelect?: (file: FileObject) => void;
  selectionMode?: boolean;
  allowDelete?: boolean;
}

export const StorageBrowser = ({ 
  bucketName, 
  onFileSelect, 
  selectionMode = false,
  allowDelete = false 
}: StorageBrowserProps) => {
  const [files, setFiles] = useState<FileObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<FileObject | null>(null);
  const { toast } = useToast();

  const { previewFile, downloadFile, deleteFile } = useFileOperations(bucketName);
  
  const {
    filteredFiles,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  } = useFileFiltering({ files });

  const loadFiles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.storage
        .from(bucketName)
        .list("", {
          limit: 1000,
          sortBy: { column: "created_at", order: "desc" }
        });

      if (error) throw error;

      setFiles(data || []);
    } catch (error: any) {
      console.error('Error loading files:', error);
      toast({
        title: "Error",
        description: `Failed to load files from ${bucketName}: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [bucketName]);

  const handleFileClick = (file: FileObject) => {
    if (selectionMode) {
      setSelectedFile(file);
      onFileSelect?.(file);
    } else {
      previewFile(file);
    }
  };

  const handleDeleteFile = (file: FileObject) => {
    deleteFile(file, loadFiles);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Folder className="h-5 w-5" />
            {bucketName} Storage Browser
            <Badge variant="outline">{filteredFiles.length} files</Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadFiles}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SearchAndSortControls
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
        />

        <FileList
          files={filteredFiles}
          loading={loading}
          searchQuery={searchQuery}
          selectedFile={selectedFile}
          onFileClick={handleFileClick}
          onPreview={previewFile}
          onDownload={downloadFile}
          onDelete={allowDelete ? handleDeleteFile : undefined}
          allowDelete={allowDelete}
        />
      </CardContent>
    </Card>
  );
};
